/**
 * The one HTTP transport. Every feature API client is built on this and nothing
 * else calls `fetch`.
 *
 * It replaces six separate clients found in the old codebase: the retrying queue in
 * `store.js`, a bare `fetch` in `media.js`, another in `integrations.js`, a
 * character-for-character copy of `getJSON` in `calendar.jsx`, one more in
 * `account.jsx`, and seven unchecked calls in `training.jsx`. None of them shared
 * retry behaviour, timeout, credentials or error handling, and two never checked
 * `response.ok`.
 *
 * Guarantees, in one place:
 *   - `response.ok` is checked exactly once, here
 *   - one error type out, always an ApiError, including for network and timeout
 *   - retry with jittered exponential backoff, and only for retryable failures
 *   - a timeout on every request
 *   - credentials always included, so the session cookie travels
 *   - caller-supplied AbortSignal respected, and never confused with a timeout
 *   - a version conflict surfaces the server's current document to the caller
 *
 * Audit references prevented here:
 *   store.js #10, #12, #13, #17  (one client, one policy, logged failures, jitter)
 *   media.js #4, integrations.js #13, calendar.jsx #5, training.jsx #2
 */

import { ApiError, API_ERROR_CODES, isApiError } from './api-error.js';
import { isErrorEnvelope, isSuccessEnvelope } from './envelope.js';

/** Attempts in total, including the first. */
export const DEFAULT_MAX_ATTEMPTS = 3;

/** Base backoff, doubled each attempt before jitter. */
export const DEFAULT_BACKOFF_MS = 400;

/**
 * Jitter fraction applied to each backoff.
 *
 * Without it, every client recovering from the same outage retries in lockstep and
 * hits the server as one wave. The old client used a flat `attempt * 1500`.
 */
export const BACKOFF_JITTER = 0.3;

/** Request timeout. Long enough for a cold serverless start, short enough to fail. */
export const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * A normal production restart briefly makes the reverse proxy return 502/503/504.
 * Safe reads wait through that window so an already-open app never needs a reload.
 */
export const PRODUCTION_READ_MAX_ATTEMPTS = 9;
export const PRODUCTION_RETRY_BUDGET_MS = 40_000;
export const MAX_BACKOFF_MS = 5_000;

/** Short-lived read cache: reopening a popup should not repeat the same database read. */
export const DEFAULT_READ_CACHE_TTL_MS = 15_000;
const readCache = new Map();
const inFlightReads = new Map();

// Bumped by clearApiReadCache(). A GET that was already in flight before a mutation
// completed is racing against stale data: it must not (a) be handed to a caller who
// asks for that same path after the mutation, and (b) be allowed to repopulate the
// cache with its pre-mutation result once it resolves. Removing it from
// `inFlightReads` handles (a); the generation stamped on its cache write handles (b).
let cacheGeneration = 0;

export function clearApiReadCache() {
  readCache.clear();
  inFlightReads.clear();
  cacheGeneration += 1;
}

function productionSafeNetworkMessage(developmentMessage) {
  return import.meta.env?.PROD ? 'Your data is temporarily unavailable. Please try again shortly.' : developmentMessage;
}

function waitForCaller(promise, signal) {
  // A caller's signal is trusted to be a real AbortSignal everywhere else in this
  // file, but a page mid-hot-reload can hand a component an event target left over
  // from a torn-down module instance - shaped enough to be truthy, missing the
  // methods an actual AbortSignal has. Treated the same as no signal at all rather
  // than thrown on, since the request itself is still perfectly able to proceed;
  // only cancellation-on-abort is what such a signal cannot actually offer.
  if (!signal || typeof signal.addEventListener !== 'function') return promise;
  if (signal.aborted) return Promise.reject(new ApiError(API_ERROR_CODES.NETWORK, { message: 'Request cancelled.' }));
  return new Promise((resolve, reject) => {
    const cancel = () => reject(new ApiError(API_ERROR_CODES.NETWORK, { message: 'Request cancelled.' }));
    signal.addEventListener('abort', cancel, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', cancel));
  });
}

/** Methods that change state, and therefore need a CSRF token. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Name of the cookie the server sets, and the header we echo it in. */
export const CSRF_COOKIE_NAME = 'eaxfuel_csrf';
export const CSRF_HEADER_NAME = 'x-csrf-token';
export const API_MUTATION_START_EVENT = 'eaxfuel:mutation-start';
export const API_MUTATION_SUCCESS_EVENT = 'eaxfuel:mutation-success';
export const API_MUTATION_ERROR_EVENT = 'eaxfuel:mutation-error';

/**
 * Any request, anywhere, just came back 401.
 *
 * The session that answered it may have been revoked from another device, ended by a
 * password change, or simply expired - this is the one place that is true regardless of
 * which feature call noticed it. Without it, only the initial session check reacted to
 * UNAUTHENTICATED; every other feature hook just showed its own generic load-failure
 * message while the rest of the app kept rendering as though still signed in.
 */
export const API_UNAUTHENTICATED_EVENT = 'eaxfuel:unauthenticated';

/**
 * A request hit a retryable failure and is about to try again, and the matching event for
 * when it has stopped retrying.
 *
 * Retrying was already happening but silently, so a flaky connection looked like a frozen
 * screen followed by an error. Announcing it lets the UI say the attempt is still going,
 * which is both truthful and usually resolves without the user doing anything.
 */
export const API_RETRY_EVENT = 'eaxfuel:request-retry';
export const API_RETRY_SETTLED_EVENT = 'eaxfuel:request-retry-settled';
let mutationSequence = 0;

function announceMutation(eventName, detail) {
  if (typeof globalThis.dispatchEvent !== 'function' || typeof globalThis.CustomEvent !== 'function') return;
  globalThis.dispatchEvent(new globalThis.CustomEvent(eventName, { detail }));
}

function backoffDelay(attempt, baseMs) {
  const exponential = baseMs * 2 ** (attempt - 1);
  const jitter = exponential * BACKOFF_JITTER * (Math.random() * 2 - 1);
  return Math.min(MAX_BACKOFF_MS, Math.max(0, Math.round(exponential + jitter)));
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new ApiError(API_ERROR_CODES.NETWORK, { message: 'Request cancelled.' }));
      },
      { once: true },
    );
  });
}

/**
 * Read the CSRF token the server placed in a readable cookie.
 *
 * Double-submit: the session cookie is HttpOnly and unreadable, this one is not, and
 * the server requires the two to agree. A cross-site form post cannot read it.
 *
 * @param {string} [cookieSource]
 * @returns {string|null}
 */
export function readCsrfToken(cookieSource = globalThis.document?.cookie ?? '') {
  for (const part of cookieSource.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === CSRF_COOKIE_NAME) return decodeURIComponent(rest.join('='));
  }
  return null;
}

/**
 * Statuses a reverse proxy or dev proxy returns when it cannot reach the API at all.
 *
 * These are not API failures, they are "the API is not there" failures, and saying so
 * is far more useful than reporting an unreadable response.
 */
const PROXY_UNREACHABLE_STATUSES = new Set([502, 503, 504]);

/**
 * Whether a response body is JSON, according to the server.
 *
 * A non-JSON body from a path under `/api/` means something other than the API
 * answered: a dev proxy error page, a load balancer, or a static index.html served by a
 * catch-all route.
 *
 * @param {Response} response
 * @returns {boolean}
 */
function looksLikeJson(response) {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('json');
}

/**
 * Turn a response into either its data or an ApiError.
 *
 * Every branch produces a message a user can act on. The vague case, "a response that
 * could not be read", is reserved for a genuine JSON parse failure from something that
 * claimed to be JSON, because every other cause is identifiable and worth naming.
 *
 * @param {Response} response
 * @returns {Promise<unknown>}
 * @throws {ApiError}
 */
async function unwrap(response) {
  if (response.status === 204) return null;

  if (response.status === 413) {
    throw new ApiError(API_ERROR_CODES.VALIDATION_FAILED, {
      message: 'That file is too large to upload. Choose a smaller image and try again.',
      details: { status: response.status },
    });
  }

  // Nothing answered from the API itself. This is the case seen in development when
  // the web server is up and the API server is not.
  if (PROXY_UNREACHABLE_STATUSES.has(response.status)) {
    throw new ApiError(API_ERROR_CODES.NETWORK, {
      message: productionSafeNetworkMessage('Could not reach the EAX FUEL server. It may be starting up or not running.'),
      details: { status: response.status },
    });
  }

  const text = await response.text();

  if (!looksLikeJson(response)) {
    // Something that is not the API answered on an API path. Most often a dev proxy
    // error page or an index.html from a catch-all route.
    throw new ApiError(API_ERROR_CODES.NETWORK, {
      message: productionSafeNetworkMessage(response.ok
        ? 'The EAX FUEL server is not answering on this address yet. It may not be running.'
        : 'Could not reach the EAX FUEL server. It may not be running.'),
      details: { status: response.status, contentType: response.headers.get('content-type') },
    });
  }

  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch (cause) {
      // It said JSON and was not. This is the genuinely unreadable case.
      throw new ApiError(API_ERROR_CODES.INTERNAL, {
        message: 'The server sent a response that could not be read.',
        cause,
      });
    }
  }

  if (response.ok) {
    if (!isSuccessEnvelope(body)) {
      throw new ApiError(API_ERROR_CODES.INTERNAL, {
        message: 'The server sent a response in an unexpected shape.',
      });
    }
    return body.meta ? { data: body.data, meta: body.meta } : body.data;
  }

  if (isErrorEnvelope(body)) {
    throw new ApiError(body.error.code, {
      message: body.error.message,
      details: body.error.details,
      correlationId: body.error.correlationId,
    });
  }

  // JSON, an error status, but not our envelope. Report by status rather than guessing,
  // so a 401 from an unexpected source still reads as "sign in" to the caller.
  const code =
    response.status === 401
      ? API_ERROR_CODES.UNAUTHENTICATED
      : response.status >= 500
        ? API_ERROR_CODES.INTERNAL
        : API_ERROR_CODES.VALIDATION_FAILED;
  throw new ApiError(code, { details: { status: response.status } });
}

/**
 * Perform one attempt, with its own timeout.
 *
 * @param {string} url
 * @param {RequestInit} init
 * @param {number} timeoutMs
 * @param {AbortSignal|undefined} callerSignal
 * @returns {Promise<unknown>}
 */
async function attemptRequest(url, init, timeoutMs, callerSignal) {
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  const signals = callerSignal ? [callerSignal, timeoutController.signal] : [timeoutController.signal];

  try {
    const response = await fetch(url, { ...init, signal: AbortSignal.any(signals) });
    return await unwrap(response);
  } catch (cause) {
    if (isApiError(cause)) throw cause;
    // A caller cancelling is not a failure to retry, a timeout is.
    if (callerSignal?.aborted) {
      throw new ApiError(API_ERROR_CODES.NETWORK, { message: 'Request cancelled.', cause });
    }
    if (timeoutController.signal.aborted) {
      throw new ApiError(API_ERROR_CODES.NETWORK, {
        message: 'The server took too long to respond.',
        cause,
      });
    }
    throw new ApiError(API_ERROR_CODES.NETWORK, { cause });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Send a request to the API.
 *
 * @param {object} params
 * @param {string} params.path            Must begin with `/api/`.
 * @param {string} [params.method]        Defaults to GET.
 * @param {unknown} [params.body]         Serialised as JSON when present.
 * @param {Record<string, string|number|boolean|undefined>} [params.query]
 * @param {AbortSignal} [params.signal]
 * @param {number} [params.timeoutMs]
 * @param {number} [params.maxAttempts]   Forced to 1 for non-idempotent methods
 *                                        unless the body carries an idempotency key.
 * @param {number} [params.retryBudgetMs] Maximum total recovery window. Primarily
 *                                        exposed for focused tests and long reads.
 * @param {number} [params.cacheTtlMs]    GET cache duration. Set to 0 for a forced refresh.
 * @returns {Promise<unknown>} The `data` field, or `{ data, meta }` for a paged list.
 * @throws {ApiError} always, for every failure mode including network and timeout.
 */
export async function apiRequest({
  path,
  method = 'GET',
  body,
  query,
  signal,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  maxAttempts,
  retryBudgetMs,
  cacheTtlMs,
}) {
  if (!path.startsWith('/api/')) {
    throw new TypeError(`API paths must begin with /api/, received ${path}`);
  }

  const url = new URL(path, globalThis.location?.origin ?? 'http://127.0.0.1');
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const upperMethod = method.toUpperCase();
  const headers = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';

  if (MUTATING_METHODS.has(upperMethod)) {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }

  const init = {
    method: upperMethod,
    headers,
    // Always, so the HttpOnly session cookie travels. The old clients sent none.
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  };

  // A retried create without an idempotency key can double-write, so it gets one
  // attempt. With a key, the server collapses the duplicate and retrying is safe.
  const isIdempotent = !MUTATING_METHODS.has(upperMethod) || Boolean(body?.idempotencyKey);
  const isProduction = import.meta.env?.PROD === true;
  // Only reads receive the longer deployment-recovery window. A write without an
  // idempotency key must never be replayed because the first attempt may have reached
  // Mongo even if its response was lost while the process restarted.
  const isSafeRead = !MUTATING_METHODS.has(upperMethod);
  const attempts = maxAttempts ?? (isSafeRead && isProduction
    ? PRODUCTION_READ_MAX_ATTEMPTS
    : isIdempotent ? DEFAULT_MAX_ATTEMPTS : 1);
  const retryDeadline = isSafeRead && isProduction
    ? Date.now() + (retryBudgetMs ?? PRODUCTION_RETRY_BUDGET_MS)
    : Number.POSITIVE_INFINITY;

  const effectiveCacheTtl = cacheTtlMs ?? (upperMethod === 'GET' && path !== '/api/auth/session' ? DEFAULT_READ_CACHE_TTL_MS : 0);
  const cacheKey = `${upperMethod} ${url.toString()}`;
  if (effectiveCacheTtl > 0) {
    const cached = readCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    if (cached) readCache.delete(cacheKey);
    const existing = inFlightReads.get(cacheKey);
    if (existing) return waitForCaller(existing, signal);
  }

  const execute = async (requestSignal) => {
    let lastError;
    // Announced once per request rather than per attempt, so the UI can show a single
    // "still trying" message and take it down again when the request settles either way.
    let announcedRetry = false;
    const settle = () => { if (announcedRetry) announceMutation(API_RETRY_SETTLED_EVENT, { path }); };
    try {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          const remainingMs = retryDeadline - Date.now();
          if (remainingMs <= 0 && lastError) throw lastError;
          return await attemptRequest(url.toString(), init, Math.min(timeoutMs, remainingMs), requestSignal);
        } catch (error) {
          lastError = error;
          // 401 is never retryable, so this is reached exactly once per request that
          // ends this way - the one choke point every GET and every write both pass
          // through, regardless of which feature call happened to notice it.
          if (isApiError(error) && error.code === API_ERROR_CODES.UNAUTHENTICATED) {
            announceMutation(API_UNAUTHENTICATED_EVENT, { path });
          }
          // The extended production policy is specifically for proxy/network failures
          // during deployment. Ordinary API 500s retain the short default retry policy.
          const errorAttemptLimit = isProduction && error?.code !== API_ERROR_CODES.NETWORK
            ? Math.min(attempts, DEFAULT_MAX_ATTEMPTS)
            : attempts;
          const canRetry = isApiError(error) && error.isRetryable && attempt < errorAttemptLimit && Date.now() < retryDeadline;
          if (!canRetry) throw error;
          if (!announcedRetry) {
            announcedRetry = true;
            announceMutation(API_RETRY_EVENT, { path, attempt, maxAttempts: errorAttemptLimit });
          }
          const delay = Math.min(backoffDelay(attempt, DEFAULT_BACKOFF_MS), retryDeadline - Date.now());
          if (delay > 0) await sleep(delay, requestSignal);
        }
      }
      throw lastError;
    } finally {
      settle();
    }
  };

  if (effectiveCacheTtl > 0) {
    // The shared request is deliberately independent of one component's lifecycle.
    // Closing one popup must not abort the fetch another visible section also needs.
    const startGeneration = cacheGeneration;
    const shared = execute(undefined).then((value) => {
      if (cacheGeneration === startGeneration) {
        readCache.set(cacheKey, { value, expiresAt: Date.now() + effectiveCacheTtl });
      }
      return value;
    }).finally(() => inFlightReads.delete(cacheKey));
    inFlightReads.set(cacheKey, shared);
    return waitForCaller(shared, signal);
  }

  if (!MUTATING_METHODS.has(upperMethod)) return execute(signal);
  const requestId = ++mutationSequence;
  const operation = path === '/api/backup/import' ? body?.mode
    : path.startsWith('/api/reminders/occurrences/') ? body?.action : null;
  announceMutation(API_MUTATION_START_EVENT, { requestId, method: upperMethod, path, operation });
  try {
    const result = await execute(signal);
    clearApiReadCache();
    announceMutation(API_MUTATION_SUCCESS_EVENT, { requestId, method: upperMethod, path, operation, result });
    return result;
  } catch (error) {
    announceMutation(API_MUTATION_ERROR_EVENT, { requestId, method: upperMethod, path, operation,
      message: error?.message ?? 'That action could not be completed.', code: error?.code ?? null });
    throw error;
  }
}
